/**
 * usePreventBodyScroll Hook
 *
 * Prevents body scroll when interacting with an element, typically used for
 * map/canvas components to prevent page scroll during pan/zoom gestures.
 *
 * @example
 * ```tsx
 * import { usePreventBodyScroll } from '@/hooks/usePreventBodyScroll';
 *
 * const MapComponent = () => {
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   usePreventBodyScroll(containerRef);
 *
 *   return (
 *     <div ref={containerRef}>
 *       <MapStage ... />
 *     </div>
 *   );
 * };
 * ```
 */

import { useEffect, useCallback, type RefObject } from 'react';

export interface UsePreventBodyScrollOptions {
  /** Whether to prevent scroll (default: true) */
  enabled?: boolean;

  /** Whether to allow scroll when not touching inside the element */
  allowOutsideScroll?: boolean;
}

/**
 * Hook to prevent body scroll when interacting with a specific element.
 *
 * This is particularly useful for map/canvas components where users
 * pan and zoom with touch gestures. Without this, the page would
 * scroll simultaneously with the map interaction.
 *
 * @param ref - Reference to the container element
 * @param options - Configuration options
 */
export const usePreventBodyScroll = (
  ref: RefObject<HTMLElement | null>,
  options: UsePreventBodyScrollOptions = {}
): void => {
  const { enabled = true, allowOutsideScroll = true } = options;

  /**
   * Prevent scroll on touchmove events within the element
   */
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;

      // Always prevent scroll inside the container
      if (e.cancelable) {
        e.preventDefault();
      }
    },
    [enabled]
  );

  /**
   * Prevent scroll wheel events within the element
   */
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!enabled) return;

      // Prevent page scroll when scrolling over the map
      e.preventDefault();
    },
    [enabled]
  );

  /**
   * Prevent scroll when touch starts on the element
   */
  const handleTouchStart = useCallback(() => {
    if (!enabled) return;

    // Apply touch-action: none to body temporarily to prevent scroll
    // This ensures iOS Safari doesn't scroll
    if (!allowOutsideScroll) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }
  }, [enabled, allowOutsideScroll]);

  /**
   * Restore scroll ability when touch ends
   */
  const handleTouchEnd = useCallback(() => {
    if (!allowOutsideScroll) {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, [allowOutsideScroll]);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    // Add event listeners with passive: false to allow preventDefault
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('wheel', handleWheel, { passive: false });
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    // Cleanup
    return () => {
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('wheel', handleWheel);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);

      // Ensure body scroll is restored on cleanup
      if (!allowOutsideScroll) {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    };
  }, [
    ref,
    enabled,
    handleTouchMove,
    handleWheel,
    handleTouchStart,
    handleTouchEnd,
    allowOutsideScroll,
  ]);
};

export default usePreventBodyScroll;
